<template>
    <div id="banned-visitors">
        <supsis-table
            :columns="getColumns()"
            :rows="bannedVisitors.data"
            :bottom-pagination="true"
            :pagination-cursor="paginationCursor"
            :options="tableOptions"
            @cursor-changed="(cursor) => (paginationCursor = cursor)"
        >
            <template #header>
                <div class="d-flex justify-content-between align-items-center">
                    <a-input-search
                        v-model="searchText"
                        :placeholder="$t('message.search')"
                        class="search-input"
                        @search="onSearch"
                    />
                    <SupsisButton ghost @click="isBanModalShow = true" class="block-manual-button">
                        <md-icon>add</md-icon>
                        {{ $t("message.blockManuel") }}
                    </SupsisButton>
                </div>
            </template>
        </supsis-table>
        <VisitorManuelBanModal
            :ban-modal-visible="isBanModalShow"
            @close="isBanModalShow = false"
            @update="refreshTable"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import * as _ from "lodash";
import { debounce } from "lodash";
import { log2 } from "../../../../../utils/log2";
import { maskContactInfo, isMaskContactInfo } from "../../../../../utils/utils";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import moment from "moment";

import webIcon from "../../../Visitors/components/svg/WebIcon.vue";
import whatsappIcon from "../../../Chatbot/pages/integrations/svg/WhatsappIcon.vue";
import { SUPPRESSION_API } from "../../../../../api/suppression";
import gmailIcon from "../../../Mail/icons/MailIcon.vue";
import VisitorManuelBanModal from "../../../../../components/VisitorManuelBanModal.vue";
import SupsisButton from "../../components/SupsisButton.vue";
import platformIconViewer from "../../../../../components/common/PlatformIconViewer.vue";

const log = log2("BannedVisitors");
export default {
    name: "BannedVisitors",
    components: {
        SupsisButton,
        SupsisTable,
        webIcon,
        whatsappIcon,
        gmailIcon,
        VisitorManuelBanModal,
        platformIconViewer,
    },
    data() {
        return {
            searchTimeout: null,
            isBanModalShow: false,
            isBusy: true,
            pageSizes: ["10", "25", "50", "100"],
            searchText: "",
            paginationCursor: {
                page: 1,
                perPage: 10,
                total: 10,
            },
            bannedVisitors: {
                data: [],
                total: 0,
            },
            cols: () => [
                {
                    field: "email",
                    key: "email",
                    title: this.$t("message.email"),
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: this.maskField('email', row.email) }}>{this.maskField('email', row.email)}</div>;
                    },
                },
                {
                    field: "fullname",
                    key: "fullname",
                    title: this.$t("message.fullname"),
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.fullname }}>{row.fullname}</div>;
                    },
                },
                {
                    field: "country",
                    key: "country",
                    title: this.$t("message.country"),
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.country }}>{row.country}</div>;
                    },
                },
                {
                    field: "city",
                    key: "city",
                    title: this.$t("message.city"),
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.city }}>{row.city}</div>;
                    },
                },
                {
                    field: "browser",
                    key: "browser",
                    title: this.$t("message.browser"),
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.browser }}>{row.browser}</div>;
                    },
                },
                {
                    field: "os",
                    key: "os",
                    title: this.$t("message.os"),
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.os }}>{row.os}</div>;
                    },
                },
                {
                    field: "connectionTime",
                    key: "connectionTime",
                    title: this.$t("message.connectionDate"),
                    renderBodyCell: ({ row }) => {
                        return this.$moment(row.connectionTime).format("D MMMM YYYY, HH:mm");
                    },
                },
                {
                    field: "blockBy",
                    key: "blockBy",
                    title: this.$t("message.blockBy"),
                    renderBodyCell: ({ row }) => {
                        return row.blockBy.name;
                    },
                },
                {
                    align: "right",
                    field: "actionsSlot",
                    key: "actionsSlot",
                    title: this.$t("message.transactions"),
                    fixed: "right",
                    renderBodyCell: ({ row }) => {
                        const renderEditButton = () => {
                            return (
                                (this.isRoot ||
                                    this.user.role.permissions.map((x) => x.name).includes("visitor.unblock")) && (
                                    <a-button
                                        v-tippy={{
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                        }}
                                        content={this.$t("message.removeBanDescription")}
                                        class="tableActionButton"
                                        shape="circle"
                                        size="small"
                                        type="primary"
                                        on-click={() => this.unban(row._id)}
                                    >
                                        <div class="material-icons">lock_open</div>
                                    </a-button>
                                )
                            );
                        };

                        return <div class="d-flex w-100 justify-content-end">{renderEditButton()}</div>;
                    },
                },
            ],
            tableOptions: {
                rowStyleOption: {
                    stripe: true,
                },
                borderX: true,
                borderY: true,
                rowKeyFieldName: "_id",
                class: "ban-list-table",
                scrollWidth: "1130px",
                virtualScrollOptions: { itemSize: 50, height: 500 },
            },
            currentSort: {
                columnKey: "connectionTime",
                order: "ascend",
            },
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            isAdmin: (state) => state.auth.adminUser != null,
            user: (state) => state.auth.user,
            isRoot: (state) => state.auth.user.role.isRoot,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
    },
    methods: {
        isMaskContactInfo,
        maskField(key, val) {
            return isMaskContactInfo(key) ? maskContactInfo(val) : val;
        },
        ...mapActions({
            unbanVisitor: "visitor/unbanVisitor",
            addNewWindow: "app/addNewWindow",
        }),
        getColumns() {
            console.log("getColumns", this.users);
            return [
                {
                    field: "platform",
                    key: "platform",
                    title: this.$t("message.platform"),
                    align: "Center",
                    fixed: "left",
                    width: 110,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <div v-tippy={{ content: row.type[0].toUpperCase() + row.type.slice(1) }}>
                                <platformIconViewer platform={row.type} />
                            </div>
                        );
                    },
                },
                {
                    key: "email",
                    title: this.$t("message.email"),
                    align: "left",
                    width: 100,
                    fixed: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                directives: [
                                    {
                                        name: "tippy",
                                        value: {
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                            content: this.maskField('email', row.visitor ? row.visitor.email : row.email),
                                        },
                                    },
                                ],
                            },
                            this.maskField('email', row.visitor ? row.visitor.email : row.email)
                        );
                    },
                },
                {
                    key: "fullname",
                    title: this.$t("message.fullname"),
                    align: "left",
                    fixed: "left",
                    width: 100,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                directives: [
                                    {
                                        name: "tippy",
                                        value: {
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                            content: row.visitor?.fullname,
                                        },
                                    },
                                ],
                            },
                            row.visitor?.fullname
                        );
                    },
                },
                {
                    key: "number",
                    title: this.$t("settings.phoneNumber"),
                    align: "left",
                    width: 120,
                    fixed: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                directives: [
                                    {
                                        name: "tippy",
                                        value: {
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                            content: this.maskField('whatsappNumber', row.whatsapp ? row.whatsapp : row.whatsappNumber),
                                        },
                                    },
                                ],
                            },
                            this.maskField('whatsappNumber', row.whatsapp ? row.whatsapp : row.whatsappNumber)
                        );
                    },
                },
                {
                    key: "ipAddress",
                    title: this.$t("userInfoWidget.IPAddress"),
                    align: "left",
                    width: 120,
                    fixed: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                directives: [
                                    {
                                        name: "tippy",
                                        value: {
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                            content: row.visitor ? row.visitor.ipAddress : row.ipAddress,
                                        },
                                    },
                                ],
                            },
                            row.visitor ? row.visitor.ipAddress : row.ipAddress
                        );
                    },
                },
                {
                    key: "country",
                    title: this.$t("message.country"),
                    align: "left",
                    width: 100,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                directives: [
                                    {
                                        name: "tippy",
                                        value: {
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                            content: row.visitor?.country,
                                        },
                                    },
                                ],
                            },
                            row.visitor?.country
                        );
                    },
                },
                {
                    key: "city",
                    title: this.$t("message.city"),
                    align: "left",
                    width: 100,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                directives: [
                                    {
                                        name: "tippy",
                                        value: {
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                            content: row.visitor?.city,
                                        },
                                    },
                                ],
                            },
                            row.visitor?.city
                        );
                    },
                },
                {
                    key: "browser",
                    title: this.$t("message.browser"),
                    align: "left",
                    width: 100,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                directives: [
                                    {
                                        name: "tippy",
                                        value: {
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                            content: row.visitor?.browser,
                                        },
                                    },
                                ],
                            },
                            row.visitor?.browser
                        );
                    },
                },
                {
                    key: "os",
                    title: this.$t("message.os"),
                    align: "left",
                    width: 100,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                directives: [
                                    {
                                        name: "tippy",
                                        value: {
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                            content: row.visitor?.os,
                                        },
                                    },
                                ],
                            },
                            row.visitor?.os
                        );
                    },
                },
                {
                    field: "connectionTime",
                    key: "connectionTime",
                    title: this.$t("message.connectionDate"),
                    align: "left",
                    width: 120,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{moment(row.createdAt).format("D MMMM YYYY, HH:mm")}</span>;
                    },
                },
                {
                    key: "createdBy",
                    title: this.$t("message.blockBy"),
                    align: "left",
                    width: 110,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                directives: [
                                    {
                                        name: "tippy",
                                        value: {
                                            followCursor: true,
                                            placement: "bottom-end",
                                            theme: this.isDarkMode ? "light-border" : "google",
                                            content:
                                                row.createdBy?.type === "user"
                                                    ? row.createdBy.user.email
                                                    : row.createdBy?.type || "N/A",
                                        },
                                    },
                                ],
                            },
                            row.createdBy?.type === "user" ? row.createdBy.user.name : row.createdBy?.type || "N/A"
                        );
                    },
                },
                {
                    field: "sending",
                    key: "Sending",
                    title: this.$t("message.sendingAllow"),
                    align: "center",
                    width: 110,
                    fixed: "right",
                    renderBodyCell: ({ row, column, rowIndex }) => {
                        return (
                            <a-switch
                                on-change={() => this.suppressionProcess(row, "outbound")}
                                checked-children="✖"
                                un-checked-children="✔"
                                checked={row.outbound}
                                value={row.outbound}
                            />
                        );
                    },
                },
                {
                    field: "receiving",
                    key: "Receiving",
                    title: this.$t("message.receiving"),
                    align: "center",
                    width: 110,
                    fixed: "right",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <a-switch
                                on-change={() => this.suppressionProcess(row, "inbound")}
                                checked-children="✖"
                                un-checked-children="✔"
                                checked={row.inbound}
                                value={row.inbound}
                            />
                        );
                    },
                },
                {
                    field: "transactions",
                    key: "transactions",
                    title: this.$t("message.transactions"),
                    scopedSlots: { customRender: "actionsSlot" },
                    width: 100,
                    fixed: "right",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        if (row.type === "web-visitor") {
                            return (
                                <div class="actions">
                                    <SupsisButton onClick={() => this.viewContactProfile(row.visitor)} small>
                                        <i class="material-icons text-primary">contact_page</i>
                                    </SupsisButton>
                                </div>
                            );
                        }
                    },
                },
            ];
        },
        refreshTable() {
            setTimeout(() => {
                this.get();
            }, 1000);
        },
        suppressionProcess(row, direction) {
            row[direction] = !row[direction];
            let value;
            if (row.type === "web-visitor") {
                value = row.visitor?._id;
            } else if (row.type === "web-visitor-ip") {
                value = row.ipAddress;
            } else if (row.type === "whatsapp") {
                value = row.whatsapp;
            } else if (row.type === "email") {
                value = row.email;
            }
            if (!row?.outbound && !row?.inbound) {
                SUPPRESSION_API.remove(row.type, value).then(() => {
                    this.$message.success({
                        content: this.$t("chatbotv2.suppressionRemoved"),
                        duration: 2,
                        key: "saved",
                    });
                    this.refreshTable();
                });
            } else {
                SUPPRESSION_API.update(row.type, value, !!row.inbound, !!row.outbound, this.user._id);
            }
        },
        tableChange(pagination, filters, sorter) {
            log("tableChange", { pagination, filters, sorter });
            this.perPage = pagination.pageSize;
            log("tableChange", { pagination, filters, sorter });
            const newSort = {
                columnKey: sorter.columnKey,
                order: sorter.order,
            };
            const oldSort = this.currentSort;
            log("show me current Sort", {
                currentSort: this.currentSort,
                newSort,
            });
            if (!_.isEqual(newSort, oldSort)) {
                this.currentSort = newSort;
                this.renderCount += 1;
                this.$forceUpdate();
            }
        },
        getFilteredRows() {
            return this.searchText
                ? this.bannedVisitors.data.filter(
                      (x) =>
                          (x.email &&
                              x.email
                                  .toLocaleLowerCase("tr-TR")
                                  .includes(this.searchText.toLocaleLowerCase("tr-TR"))) ||
                          (x.visitor?.email &&
                              x.visitor?.email
                                  .toLocaleLowerCase("tr-TR")
                                  .includes(this.searchText.toLocaleLowerCase("tr-TR"))) ||
                          (x.visitor?.fullname &&
                              x.visitor?.fullname
                                  .toLocaleLowerCase("tr-TR")
                                  .includes(this.searchText.toLocaleLowerCase("tr-TR"))) ||
                          (x.browser &&
                              x.browser
                                  .toLocaleLowerCase("tr-TR")
                                  .includes(this.searchText.toLocaleLowerCase("tr-TR"))) ||
                          (x.city &&
                              x.city.toLocaleLowerCase("tr-TR").includes(this.searchText.toLocaleLowerCase("tr-TR"))) ||
                          (x.country &&
                              x.country
                                  .toLocaleLowerCase("tr-TR")
                                  .includes(this.searchText.toLocaleLowerCase("tr-TR"))) ||
                          (x.os &&
                              x.os.toLocaleLowerCase("tr-TR").includes(this.searchText.toLocaleLowerCase("tr-TR"))) ||
                          (x.ipAddress &&
                              x.ipAddress
                                  .toLocaleLowerCase("tr-TR")
                                  .includes(this.searchText.toLocaleLowerCase("tr-TR"))) ||
                          (x.whatsapp &&
                              x.whatsapp
                                  .toLocaleLowerCase("tr-TR")
                                  .includes(this.searchText.toLocaleLowerCase("tr-TR")))
                  )
                : this.bannedVisitors.data;
        },
        onSearch: debounce(function (value) {
            this.searchText = value;
            this.get();
        }, 1000),

        async get() {
            this.isBusy = true;
            const response = await SUPPRESSION_API.list(this.searchText, {
                page: this.paginationCursor.page,
                perPage: this.paginationCursor.perPage,
            });
            this.bannedVisitors.data = response.data.contacts;
            this.paginationCursor.total = response.data.total;
            this.isBusy = false;
        },
        viewContactProfile(item) {
            this.addNewWindow({
                type: "show-visitor",
                title: `${item.fullname} Profile`,
                payload: {
                    visitorId: item._id,
                },
                footer: false,
            });
        },
    },
    async mounted() {
        this.get();
        setTimeout(() => {
            console.log("Banned Visitor List", { visitors: this.bannedVisitors.data });
        }, 1000);
    },
    watch: {
        paginationCursor: {
            deep: true,
            handler() {
                this.get();
            },
        },
        searchText: {
            handler(newVal, oldVal) {
                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout);
                }

                this.searchTimeout = setTimeout(() => {
                    this.get();
                }, 600);
            },
            immediate: false,
        },
    },
};
</script>

<style lang="scss">
#banned-visitors {
    position: relative;
    height: 100%;

    .ant-switch-checked {
        background: #cc0100 !important;
    }

    .ant-switch {
        background: green;
    }

    .ban-modal-icon {
        width: 20px;
        height: 20px;
    }

    .banlist-web-icon {
        width: 30px;
        height: 30px;
    }

    .actions {
        .supsis-button {
            background: transparent !important;
            border: none !important;
        }
    }
}
</style>
